import { Injectable } from '@angular/core';
import {
  BirthdayPromotion,
  BonusPointReward,
  CustomerTypePromotion,
  RedeemableDiscounts,
  ReminderPromotion,
} from '../../models';
import { RewardUtils } from '../../utils';
import { PrivateServerCallerService } from '../server-caller/private-server-caller.service';

@Injectable({
  providedIn: 'root',
})
export class RewardService {
  constructor(private privateServerCallerService: PrivateServerCallerService) {}

  // region Reward

  public async getAllBonusPointRewardsOfBusinessObject(
    businessObjectId: number
  ): Promise<BonusPointReward[]> {
    const url = `/v1/business-object/${businessObjectId}/reward/bonus-point-based-discounts`;

    const response = await this.privateServerCallerService.get(url);

    const rewards: BonusPointReward[] = response?.body;

    return RewardUtils.transformRewardListForFrontend(
      rewards,
      businessObjectId
    );
  }

  public async createBonusPointReward(
    reward: BonusPointReward
  ): Promise<BonusPointReward> {
    const url = `/v1/business-object/${reward.businessObjectId}/reward/bonus-point-based-discount`;

    const response = await this.privateServerCallerService.post(
      url,
      RewardUtils.transformBonusPointRewardForBackend(reward)
    );

    return response?.body;
  }

  public async updateBonusPointReward(reward: BonusPointReward): Promise<any> {
    const url = `/v1/business-object/${reward.businessObjectId}/reward/bonus-point-based-discount/${reward.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      RewardUtils.transformBonusPointRewardForBackend(reward)
    );

    return response?.status;
  }

  public async updateBonusPointBalance(
    customerId: number,
    change: number
  ): Promise<boolean> {
    const url = `/v1/customer/${customerId}/bonuspoint-balance`;

    const response = await this.privateServerCallerService.put(url, { change });

    return response?.status === 200;
  }

  // endregion

  // region Reminder

  public async getAllReminderPromotionsOfBusinessObject(
    businessObjectId: number
  ): Promise<ReminderPromotion[]> {
    const url = `/v1/business-object/${businessObjectId}/promotions/reminder`;

    const response = await this.privateServerCallerService.get(url);

    return response?.body;
  }

  public async createReminderPromotion(
    promotion: ReminderPromotion
  ): Promise<ReminderPromotion> {
    const url = `/v1/business-object/${promotion.businessObjectId}/promotion/reminder`;

    const response = await this.privateServerCallerService.post(
      url,
      RewardUtils.transformReminderPromotionForBackend(promotion)
    );

    return response?.body;
  }

  public async updateReminderPromotion(
    promotion: ReminderPromotion
  ): Promise<any> {
    const url = `/v1/business-object/${promotion.businessObjectId}/promotion/reminder/${promotion.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      RewardUtils.transformReminderPromotionForBackend(promotion)
    );

    return response.status;
  }

  // endregion

  // region Promotion

  public async getAllCustomerTypePromotionsOfBusinessObject(
    businessObjectId: number
  ): Promise<CustomerTypePromotion[]> {
    const url = `/v1/business-object/${businessObjectId}/promotions/customer-type-based`;

    const response = await this.privateServerCallerService.get(url);

    return RewardUtils.transformCustomerTypePromotionListForFrontend(
      response?.body
    );
  }

  public async createCustomerTypePromotion(
    promotion: CustomerTypePromotion
  ): Promise<CustomerTypePromotion> {
    const url = `/v1/business-object/${promotion.businessObjectId}/promotion/customer-type-based`;

    const response = await this.privateServerCallerService.post(
      url,
      RewardUtils.transformCustomerTypePromotionForBackend(promotion)
    );

    return RewardUtils.transformCustomerTypePromotionForFrontend(
      response?.body
    );
  }

  public async updatePrivateCustomerTypePromotion(
    promotion: CustomerTypePromotion
  ): Promise<any> {
    const url = `/v1/business-object/${promotion.businessObjectId}/private-customer-type-based-promotions/${promotion.id}`;

    const response = await this.privateServerCallerService.patch(
      url,
      RewardUtils.transformCustomerTypePromotionForBackend(promotion)
    );

    return response?.body;
  }

  // endregion

  // region Birthday

  public async getAllBirthdayPromotionsOfBusinessObject(
    businessObjectId: number
  ): Promise<BirthdayPromotion[]> {
    const url = `/v1/business-object/${businessObjectId}/promotions/birthday`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async createBirthdayPromotion(
    birthdayPromotion: BirthdayPromotion
  ): Promise<BirthdayPromotion> {
    const url = `/v1/business-object/${birthdayPromotion.businessObjectId}/promotion/birthday`;

    const response = await this.privateServerCallerService.post(
      url,
      RewardUtils.transformBirthdayPromotionForBackend(birthdayPromotion)
    );
    return response?.body;
  }

  public async updateBirthdayPromotion(
    birthdayPromotion: BirthdayPromotion
  ): Promise<any> {
    const url = `/v1/business-object/${birthdayPromotion.businessObjectId}/promotion/birthday/${birthdayPromotion.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      RewardUtils.transformBirthdayPromotionForBackend(birthdayPromotion)
    );
    return response?.status;
  }

  // endregion

  // region RedeemableDiscounts

  public async getRedeemableDiscounts(
    boId: number,
    customerId: number
  ): Promise<RedeemableDiscounts> {
    const url = `/v1/business-object/${boId}/customer/${customerId}/redeemable-discounts`;

    const response = await this.privateServerCallerService.get(url);
    return RewardUtils.transformRedeemableDiscountsForFrontend(response?.body);
  }

  // endregion
}
